import { AdminPage, AdminPageHeader } from '../../components/layout/AdminPage';
/**
 * @page PlayerRegistrationPage
 * @description 玩家報名/報到管理（UI-2-4）
 * 搜尋會員報名、確認報到、查看玩家列表
 */

import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { ActionKey, type PlayerEntryDto } from '@pokerroom/types';
import { isAxiosError } from 'axios';
import { api } from '../../lib/api.ts';

import EntryCashierPanel from './EntryCashierPanel';
import { useAuthStore } from '../../store/auth.store';

interface MemberSearchResult {
  member: { id: string; nickname: string; memberNo: string; phone?: string; status?: string };
  hasDebt: boolean;
  isBlacklisted: boolean;
  isInactive: boolean;
}

async function searchMembers(q: string): Promise<MemberSearchResult[]> {
  if (q.length < 1) return [];
  return api.get<MemberSearchResult[]>(`/members/check-in/search?q=${encodeURIComponent(q)}`);
}

async function fetchEntries(tournamentId: string): Promise<PlayerEntryDto[]> {
  return api.get<PlayerEntryDto[]>(`/tournament/${tournamentId}/entries`);
}

async function registerPlayer(
  tournamentId: string,
  dto: { memberId?: string; playerName?: string },
) {
  return api.post(`/tournament/${tournamentId}/entries`, dto);
}

async function checkInPlayer(tournamentId: string, entryId: string) {
  return api.post(`/tournament/${tournamentId}/entries/${entryId}/check-in`);
}

async function cancelEntry(tournamentId: string, entryId: string) {
  return api.delete(`/tournament/${tournamentId}/entries/${entryId}`);
}

export default function PlayerRegistrationPage() {
  const { id: tournamentId } = useParams<{ id: string }>();
  const { t } = useTranslation('tournament');
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const actions = useAuthStore((s) => s.allowedActions);
  const system = useAuthStore((s) => s.user?.isSystemUser);
  const actorId = useAuthStore((s) => s.user?.id);
  const canCashier = !!system || !!actions?.includes(ActionKey.MEMBER_CHARGE_READ);
  const [cashier, setCashier] = useState<{ id: string; name: string } | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [anonName, setAnonName] = useState('');
  const [searchResults, setSearchResults] = useState<MemberSearchResult[]>([]);
  const [error, setError] = useState('');
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();

  const { data: entries = [] } = useQuery({
    queryKey: ['tournament-entries', tournamentId],
    queryFn: () => fetchEntries(tournamentId!),
    enabled: !!tournamentId,
    refetchInterval: 3000,
  });

  // Debounced search
  const handleSearch = (q: string) => {
    setSearchQuery(q);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => setDebouncedQuery(q), 300);
  };

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  useEffect(() => {
    if (debouncedQuery.length < 1) {
      setSearchResults([]);
      return;
    }
    searchMembers(debouncedQuery)
      .then(setSearchResults)
      .catch((err) =>
        console.warn('[Registration] search failed:', err instanceof Error ? err.message : err),
      );
  }, [debouncedQuery]);

  const registerMutation = useMutation({
    mutationFn: (dto: { memberId?: string; playerName?: string }) =>
      registerPlayer(tournamentId!, dto),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['tournament-entries', tournamentId] });
      setSearchQuery('');
      setAnonName('');
      setSearchResults([]);
      setError('');
    },
    onError: (err: Error) =>
      setError(
        isAxiosError(err) && typeof err.response?.data?.message === 'string'
          ? err.response.data.message
          : err.message,
      ),
  });

  const checkInMutation = useMutation({
    mutationFn: (entryId: string) => checkInPlayer(tournamentId!, entryId),
    onSuccess: () =>
      void queryClient.invalidateQueries({ queryKey: ['tournament-entries', tournamentId] }),
    onError: (err: Error) => setError(err.message),
  });

  const cancelMutation = useMutation({
    mutationFn: (entryId: string) => cancelEntry(tournamentId!, entryId),
    onSuccess: () =>
      void queryClient.invalidateQueries({ queryKey: ['tournament-entries', tournamentId] }),
    onError: (err: Error) =>
      setError(
        isAxiosError(err) && typeof err.response?.data?.message === 'string'
          ? err.response.data.message
          : err.message,
      ),
  });

  const handleCancel = (entryId: string, playerName: string) => {
    if (!confirm(t('confirmCancelRegistration', { name: playerName }))) return;
    cancelMutation.mutate(entryId);
  };

  const checkedIn = entries.filter((e) => e.status === 'CHECKED_IN' || e.status === 'PLAYING');
  const registered = entries.filter((e) => e.status === 'REGISTERED');

  const handleConfirm = () => {
    void queryClient.invalidateQueries({ queryKey: ['tournaments'] });
    void queryClient.invalidateQueries({ queryKey: ['tournament', tournamentId] });
    navigate(`/tournament/${tournamentId}`);
  };

  return (
    <AdminPage
      contentClassName="admin-content-search"
      header={
        <AdminPageHeader
          title={
            <>
              {t('register')} / {t('checkIn')}
            </>
          }
          actions={
            <>
              <button
                onClick={handleConfirm}
                className="px-5 py-2.5 bg-primary text-primary-content rounded-xl font-bold hover:bg-primary/90 transition-colors"
              >
                {t('confirmDone')}
              </button>
            </>
          }
        />
      }
    >
      {cashier && tournamentId && canCashier && (
        <EntryCashierPanel
          key={`${actorId}:${tournamentId}:${cashier.id}`}
          memberId={cashier.id}
          memberName={cashier.name}
          tournamentId={tournamentId}
          onClose={() => setCashier(null)}
        />
      )}

      {/* 報名操作 */}
      <div className="bg-base-300 border border-base-300 rounded-xl p-4 mb-6">
        <h2 className="text-base-content font-medium mb-4">{t('registerPlayer')}</h2>

        {/* 會員搜尋 */}
        <div className="mb-4">
          <input
            value={searchQuery}
            onChange={(e) => void handleSearch(e.target.value)}
            placeholder={t('searchMemberPlaceholder')}
            className="w-full bg-base-300 text-base-content rounded-lg px-3 py-2 border border-base-300 outline-hidden focus:border-primary"
          />
          {searchResults.length > 0 && (
            <div className="mt-2 bg-gray-800 rounded-lg border border-base-300 overflow-hidden">
              {searchResults.map(({ member, hasDebt, isBlacklisted, isInactive }) => {
                const blocked = isBlacklisted || isInactive;
                return (
                  <div key={member.id}>
                    <button
                      onClick={() => {
                        if (isBlacklisted) {
                          alert(t('blacklistCannotRegister', { name: member.nickname }));
                          return;
                        }
                        if (isInactive) {
                          alert(t('inactiveCannotRegister', { name: member.nickname }));
                          return;
                        }
                        registerMutation.mutate({ memberId: member.id });
                      }}
                      className={`w-full flex items-center justify-between px-4 py-3 text-left ${blocked ? 'opacity-50 cursor-not-allowed' : 'hover:bg-white/10'}`}
                    >
                      <div>
                        <span className={`font-medium ${blocked ? 'text-white/50' : 'text-white'}`}>
                          {member.nickname}
                        </span>
                        <span className="text-base-content/70 text-sm ml-2">{member.memberNo}</span>
                        {member.phone && (
                          <span className="text-base-content/70 text-sm ml-2">{member.phone}</span>
                        )}
                      </div>
                      <div className="flex gap-2">
                        {hasDebt && (
                          <span className="text-xs bg-orange-500/20 text-orange-300 px-2 py-0.5 rounded">
                            {t('hasDebt')}
                          </span>
                        )}
                        {isInactive && (
                          <span className="text-xs bg-gray-500/20 text-gray-300 px-2 py-0.5 rounded">
                            {t('inactive', { ns: 'common' })}
                          </span>
                        )}
                        {isBlacklisted && (
                          <span className="text-xs bg-red-500/20 text-error px-2 py-0.5 rounded">
                            {t('blacklisted')}
                          </span>
                        )}
                      </div>
                    </button>
                    {canCashier && (
                      <button
                        type="button"
                        className="px-4 py-2 text-primary"
                        disabled={!!cashier}
                        onClick={() => setCashier({ id: member.id, name: member.nickname })}
                      >
                        {t('cashier.open')}
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* 匿名玩家報名 */}
        <div className="flex gap-2">
          <input
            value={anonName}
            onChange={(e) => setAnonName(e.target.value)}
            placeholder={t('anonPlayerPlaceholder')}
            className="flex-1 bg-base-300 text-base-content rounded-lg px-3 py-2 border border-base-300 outline-hidden"
          />
          <button
            disabled={!anonName.trim() || registerMutation.isPending}
            onClick={() => registerMutation.mutate({ playerName: anonName.trim() })}
            className="px-4 py-2 bg-primary text-primary-content rounded-lg disabled:opacity-50"
          >
            {t('registerBtn')}
          </button>
        </div>

        {error && <p className="text-error text-sm mt-2">{error}</p>}
      </div>

      {/* 統計 */}
      <div className="grid grid-cols-3 gap-3 mb-6">
        <div className="bg-base-300 border border-base-300 rounded-xl p-3 text-center">
          <div className="text-2xl font-bold text-base-content">{entries.length}</div>
          <div className="text-base-content/70 text-xs mt-1">{t('totalRegistered')}</div>
        </div>
        <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-xl p-3 text-center">
          <div className="text-2xl font-bold text-success">{checkedIn.length}</div>
          <div className="text-success/70 text-xs mt-1">{t('checkedInCount')}</div>
        </div>
        <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-xl p-3 text-center">
          <div className="text-2xl font-bold text-warning">{registered.length}</div>
          <div className="text-warning/70 text-xs mt-1">{t('pendingCheckIn')}</div>
        </div>
      </div>

      {/* 待報到清單 */}
      {registered.length > 0 && (
        <div className="bg-base-300 border border-base-300 rounded-xl mb-4">
          <div className="p-4 border-b border-base-300">
            <h2 className="text-base-content font-medium">{t('pendingCheckInPlayers')}</h2>
          </div>
          <div className="divide-y divide-white/5">
            {registered.map((entry) => (
              <div key={entry.id} className="flex items-center justify-between px-4 py-3">
                <span className="text-base-content">{entry.playerName}</span>
                <div className="flex gap-2">
                  {canCashier && entry.memberId && (
                    <button
                      disabled={!!cashier}
                      onClick={() => setCashier({ id: entry.memberId!, name: entry.playerName })}
                    >
                      {t('cashier.open')}
                    </button>
                  )}
                  <button
                    onClick={() => checkInMutation.mutate(entry.id)}
                    disabled={checkInMutation.isPending}
                    className="px-3 py-1 bg-emerald-600/20 text-success rounded-lg text-sm hover:bg-emerald-600/40"
                  >
                    {t('confirmCheckIn')}
                  </button>
                  <button
                    onClick={() => handleCancel(entry.id, entry.playerName)}
                    disabled={cancelMutation.isPending}
                    className="px-3 py-1 bg-red-600/20 text-error rounded-lg text-sm hover:bg-red-600/40"
                  >
                    {t('cancelRegistration')}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* 已報到清單 */}
      {checkedIn.length > 0 && (
        <div className="bg-base-300 border border-base-300 rounded-xl">
          <div className="p-4 border-b border-base-300">
            <h2 className="text-base-content font-medium">{t('checkedInPlayers')}</h2>
          </div>
          <div className="divide-y divide-white/5">
            {checkedIn.map((entry) => (
              <div key={entry.id} className="flex items-center justify-between px-4 py-3">
                <span className="text-base-content">{entry.playerName}</span>
                <div className="flex gap-2 items-center">
                  {canCashier && entry.memberId && (
                    <button
                      disabled={!!cashier}
                      onClick={() => setCashier({ id: entry.memberId!, name: entry.playerName })}
                    >
                      {t('cashier.open')}
                    </button>
                  )}
                  <span className="px-2 py-0.5 bg-emerald-500/20 text-success rounded text-xs">
                    {t(`playerStatus.${entry.status}`)}
                  </span>
                  {entry.status === 'CHECKED_IN' && (
                    <button
                      onClick={() => handleCancel(entry.id, entry.playerName)}
                      disabled={cancelMutation.isPending}
                      className="px-3 py-1 bg-red-600/20 text-error rounded-lg text-sm hover:bg-red-600/40"
                    >
                      {t('cancel', { ns: 'common' })}
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </AdminPage>
  );
}
